/**
 * Toolbar note ("scratchpad") model — a TOT-style menu-bar scratchpad of seven fixed, color-coded
 * dots. The dot CONTENT is stored as ordinary synced Yjs docs (noteIds `toolbar-1`…`toolbar-7`) that
 * ride the existing sync transport but are kept out of the notes registry/search by the
 * `isToolbarNoteId` guard (see server-sync's store hook). This module owns only the user-facing
 * CONFIG — enable state, the summon hotkey, and per-dot color/mode — which is persisted server-side
 * (one doc per user) so it follows the user across devices.
 */

/** How a dot renders its markdown: formatted (rich) or raw source (plain). */
export type ToolbarDotMode = 'rich' | 'plain';

/** Fixed number of scratchpad dots, à la TOT. Not user-configurable by design. */
export const TOOLBAR_DOT_COUNT = 7;

/** Shared noteId prefix for the dot docs — the single source of truth both the sync server (to skip
 * the registry) and the client (to open the docs) agree on. */
export const TOOLBAR_NOTE_ID_PREFIX = 'toolbar-';

/** The synced noteId backing dot `index` (0-based). */
export function toolbarDotNoteId(index: number): string {
  return `${TOOLBAR_NOTE_ID_PREFIX}${index + 1}`;
}

/** True for any noteId backing a toolbar dot. Used to exclude these docs from the notes registry,
 * search re-embedding, and wake-and-sync push (they are a private scratchpad, not a page). */
export function isToolbarNoteId(noteId: string): boolean {
  return noteId.startsWith(TOOLBAR_NOTE_ID_PREFIX);
}

/** Per-dot presentation. Content lives in the dot's Yjs doc, not here. */
export interface ToolbarDotConfig {
  /** Accent color (CSS hex) shown on the dot chip. */
  color: string;
  /** Whether this dot shows formatted or raw markdown. */
  mode: ToolbarDotMode;
}

export interface ToolbarConfig {
  /** Master switch for the menu-bar scratchpad surface. */
  enabled: boolean;
  /** Global-shortcut accelerator that summons the popover (Tauri accelerator syntax). */
  hotkey: string;
  /** Mode applied to a dot that has no explicit mode yet (and to newly seeded configs). */
  defaultMode: ToolbarDotMode;
  /** Exactly TOOLBAR_DOT_COUNT dots, in display order. */
  dots: ToolbarDotConfig[];
}

/** The seven default dot colors (TOT-like spectrum). */
const DEFAULT_DOT_COLORS = [
  '#e5484d', // red
  '#f76808', // orange
  '#ffb224', // amber
  '#30a46c', // green
  '#12a594', // teal
  '#0091ff', // blue
  '#8e4ec6', // purple
];

/** Default config: enabled, Cmd/Ctrl+Shift+Space summon, rich by default, the seven colors. */
export function defaultToolbarConfig(): ToolbarConfig {
  return {
    enabled: true,
    hotkey: 'CmdOrCtrl+Shift+Space',
    defaultMode: 'rich',
    dots: DEFAULT_DOT_COLORS.map((color) => ({ color, mode: 'rich' as ToolbarDotMode })),
  };
}

function isMode(v: unknown): v is ToolbarDotMode {
  return v === 'rich' || v === 'plain';
}

/**
 * Coerce untrusted input (a PUT body) into a valid ToolbarConfig, filling any missing or malformed
 * field from the defaults. Always returns exactly TOOLBAR_DOT_COUNT dots so the UI can index safely.
 */
export function coerceToolbarConfig(input: unknown): ToolbarConfig {
  const d = defaultToolbarConfig();
  if (!input || typeof input !== 'object') return d;
  const o = input as Record<string, unknown>;
  const defaultMode = isMode(o.defaultMode) ? o.defaultMode : d.defaultMode;
  const inDots = Array.isArray(o.dots) ? o.dots : [];
  const dots: ToolbarDotConfig[] = d.dots.map((fallback, i) => {
    const raw = (inDots[i] ?? {}) as Record<string, unknown>;
    return {
      color: typeof raw.color === 'string' ? raw.color : fallback.color,
      mode: isMode(raw.mode) ? raw.mode : defaultMode,
    };
  });
  return {
    enabled: typeof o.enabled === 'boolean' ? o.enabled : d.enabled,
    hotkey: typeof o.hotkey === 'string' && o.hotkey.trim() ? o.hotkey : d.hotkey,
    defaultMode,
    dots,
  };
}
